import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, Renderer2, computed, effect, input, signal } from '@angular/core';

const ELLIPSIS_SYMBOL = '...';
const UNIQE_DIVIDER = '*'; // The displayed content elements might have the same character as the desired divider, which fails the calulcation so we use an uniqe one.
const replaceCharacter = new RegExp(`["${UNIQE_DIVIDER}]`, 'g');
/**
 * It can happen that the actual font-size + font-type is bigger than the line-height which results with an overflow by default when we take measurements.
 * To avoid these edge cases we introduced a threshold value which is in pixels. With this threshold in calculation the small difference which comes from
 * font-types are don't matter anymore and we don't end up with false calculations.
 * */
const LINE_HEIGHT_THRESHOLD_PX = 5;

/**
 * @description
 * Displays a list of divided data items with a predefined number of lines.
 * If the content exceeds the maximum line count, the extra items are hidden, and their count is displayed in a predefined format.
 *
 * @property {string[]} ellipsisData - Required input property to get the content for display.
 * @property {string} contentDivider - Required input property to get the divider character which separates the content.
 * @property {string} contentOverflowName - Required input property to get the text which will be the groupping name for the hidden content elements.
 * @property {number} maxLineCount - Required input property to get the line count limit for the displayed content.
 *
 * @example
 * ellipsisData = "name1, name2, name3, name4, name5, name6, name7, name8"
 * contentDivider = ","
 * contentOverflowname = "Names"
 * maxLineCount = 2
 *
 * name1, name2, name3,
 * name4, name5,... +3 Names
 */
@Component({
    selector: 'ms-ellipsis',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './ellipsis.component.scss',
    host: {
        '[style.-webkit-line-clamp]': 'maxLineCount()',
        '[style.line-clamp]': 'maxLineCount()',
    },
    template: ``,
})
export class EllipsisComponent implements OnDestroy {
    ellipsisData = input.required<string[]>();
    contentDivider = input.required<string>();
    contentOverflowName = input.required<string>();
    maxLineCount = input.required<number>();

    private contentElementsCount = computed(() => this.ellipsisData().length);
    private displayData = computed(() => this.ellipsisData().join(`${UNIQE_DIVIDER} `));

    private turncatedContent = '';
    private overFlowContent = '';
    private hasHiddenElements = false;
    private resizeObserver: ResizeObserver;

    private resizeNotifier = signal<ResizeObserverEntry[]>([]);

    constructor(
        private hostElement: ElementRef<HTMLElement>,
        private renderer: Renderer2,
    ) {
        this.resizeObserver = new ResizeObserver((element) => {
            this.resizeNotifier.set(element);
        });

        this.resizeObserver.observe(this.hostElement.nativeElement);

        effect(() => {
            this.resizeNotifier();
            this.displayContent();
        });
    }

    ngOnDestroy() {
        this.resizeObserver.disconnect();
    }

    private displayContent() {
        this.buildEllipsis();
        if (this.hasHiddenElements) {
            this.renderer.setProperty(this.hostElement.nativeElement, 'innerHTML', '');
            const overflowEllipsisElement = this.renderer.createElement('span') as HTMLElement;
            const overflowTextElement = this.renderer.createElement('span') as HTMLElement;
            this.renderer.addClass(overflowTextElement, 'overflow');
            let overflowTextElements = this.overFlowContent.split(' ');
            const ellipsisText = this.renderer.createText(overflowTextElements.shift() ?? '') as HTMLElement;
            const overflowText = this.renderer.createText(` ${overflowTextElements.join(' ')}`) as HTMLElement;

            this.renderer.appendChild(overflowEllipsisElement, ellipsisText);
            this.renderer.appendChild(overflowTextElement, overflowText);
            this.renderer.appendChild(this.hostElement.nativeElement, this.renderer.createText(this.turncatedContent));
            this.renderer.appendChild(this.hostElement.nativeElement, overflowEllipsisElement);
            this.renderer.appendChild(this.hostElement.nativeElement, overflowTextElement);
        }
    }

    private buildEllipsis() {
        this.renderer.setProperty(this.hostElement.nativeElement, 'innerHTML', this.displayData().replace(replaceCharacter, this.contentDivider()));

        if (this.isOverflowed(this.hostElement.nativeElement)) {
            const contentArray = this.displayData().split(' ');
            let moreText = '';

            const contentArrayStartingLength = contentArray.length;
            const initialMaxHeight = this.hostElement.nativeElement.clientHeight;

            for (let i = 0; i < contentArrayStartingLength; i++) {
                contentArray.pop();
                const contentData = contentArray.join(' ');
                let content = contentData + ELLIPSIS_SYMBOL + moreText;
                const displayedContentElementsCount = content.split(UNIQE_DIVIDER).length;
                const hiddenElments = this.contentElementsCount() - displayedContentElementsCount + 1;
                if (hiddenElments > 0) {
                    this.hasHiddenElements = true;
                    moreText = ` +${hiddenElments} ${this.contentOverflowName()}`;
                    this.overFlowContent = ELLIPSIS_SYMBOL + moreText;
                    this.turncatedContent = contentData.replace(replaceCharacter, this.contentDivider());
                } else {
                    moreText = '';
                }
                content = contentData + ELLIPSIS_SYMBOL + moreText;

                this.renderer.setProperty(this.hostElement.nativeElement, 'innerHTML', content.replace(replaceCharacter, this.contentDivider()));

                if (this.hostElement.nativeElement.scrollHeight < initialMaxHeight || !this.isOverflowed(this.hostElement.nativeElement)) {
                    break;
                }
            }
        } else {
            this.hasHiddenElements = false;
        }
    }

    private isOverflowed(el: HTMLElement) {
        return el.scrollHeight > el.clientHeight + LINE_HEIGHT_THRESHOLD_PX;
    }
}
